import type { JSX } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useExperienceForm } from '../hooks/useExperienceForm';
import { useExperienceSubmit } from '../hooks/useExperienceSubmit';
import { ExperienceFormFields } from './ExperienceFormFields';
import type { Experience } from '../types/experience.types';
import { getFormModeConfig } from './ExperienceFormDialog.utils';

interface ExperienceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  candidateId: string;
  experience?: Experience | null;
}

function FormButtons({
  isLoading,
  submitLabel,
  onClose,
}: {
  isLoading: boolean;
  submitLabel: string;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" disabled={isLoading}>
        {submitLabel}
      </Button>
    </DialogFooter>
  );
}

export function ExperienceFormDialog({
  open,
  onOpenChange,
  mode,
  candidateId,
  experience,
}: ExperienceFormDialogProps): JSX.Element {
  const form = useExperienceForm(mode, experience);
  const handleClose = (): void => onOpenChange(false);
  const { onSubmit, isLoading } = useExperienceSubmit({
    mode,
    candidateId,
    experience,
    handleSubmit: form.handleSubmit,
    setError: form.setError,
    reset: form.reset,
    onClose: handleClose,
  });
  const { title, submitLabel, loadingLabel } = getFormModeConfig(mode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <ExperienceFormFields
            register={form.register}
            errors={form.formState.errors}
            setValue={form.setValue}
            isOngoing={form.isOngoing}
            isFullTime={form.isFullTime}
            isContractor={form.isContractor}
            continuous={form.continuous}
            isGig={form.isGig}
          />
          <FormButtons
            isLoading={isLoading}
            submitLabel={isLoading ? loadingLabel : submitLabel}
            onClose={handleClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}
